import React, { useState, useEffect } from 'react';

import { useDispatch } from 'react-redux';

import { DateTime } from 'luxon';
import { transfer, pix, availableSoon, pay } from '@portal/config/app.json';

import NavigationService from '~/services/navigation';

import {
  DetailsAction,
  SettingsActions,
  TransactionsActions,
} from '@portal/actions';

import Barcode from '@portal/assets/svg/pay-icons/ic_barcode.svg?react';
import Pix from '@portal/assets/svg/pay-icons/ic_pix.svg?react';

import AdvancedModal from '@portal/components/AdvancedModal/AdvancedModal';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import BilletSlipCode from '@portal/components/BilletSlipCode/BilletSlipCode';
import PaymentDetails from '@portal/components/PaymentDetails/PaymentDetails';
import * as MessageService from '@portal/services/message';
import useReduxState from '@portal/hooks/useReduxState';
import Billet from '@portal/utils/billet';
import { TransactionStatus } from '@portal/enum/approvalsType';

import { encrypt, signData } from '@portal/repositories/securitySdkConfig';
import { getRouteStackPath } from '@portal/config/routes';
import { DetailType } from '@portal/models/enumerators';
import { sendEvent } from '@portal/services/firebase';
import { unmaskField } from '@portal/services/masks';
import { EventType } from '@portal/enum/analytics';
import { translate } from '@portal/services/i18n';
import { requestTotpToken, totpTransfer } from '@portal/actions/totpActions';
import {
  SEND_TOTP_FOR_USER,
  SET_SUCCESS_PIN_FOR_TOTP,
} from '@portal/actions/actionTypes';
import MaintenanceModal from '@portal/components/MaintenanceModal/MaintenanceModal';
import { moneyFormat } from '@portal/utils/format';
import { getEnumKeyByValue } from '@portal/utils/enum';
import { BilletErrors } from '@portal/enum/errors';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedSteps from '@portal/components/AdvancedSteps/AdvancedSteps';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { useNavigation } from '@portal/hooks/useNavigation';

enum TabIndexEnum {
  ACTIONS = '1',
  BILLET_CODE = '2',
  PAYMENT_DETAILS = '3',
}

const Pay = () => {
  const dispatch = useDispatch();

  const navigate = useNavigation();

  const {
    details: { transaction, error, type },
    user: { me },
    transactions: { sendSmsForTransaction, successTransferWithTotp },
    settings: { remoteConfig },
    account: { balance },
    theme: { currentTheme },
  } = useReduxState();

  const [tabKey, setTabKey] = useState('1');
  const [paymentType, setPaymentType] = useState<'billet' | 'pix'>();
  const [code, setCode] = useState('');
  const [rawAmount, setRawAmount] = useState(
    transaction?.billetInfo?.consolidatedAmount ?? 0
  );
  const [isPinModalVisible, setIsPinModalVisible] = useState(false);
  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  const [alertText, setAlertText] = useState('');
  const [isSmsModalVisible, setIsSmsModalVisible] = useState(false);
  const [openMaintenanceModal, setOpenMaintenanceModal] = useState(false);
  const [isScheduleModalVisible, setIsScheduleModalVisible] = useState(false);
  const [transactionScheduleAt, setTransactionScheduleAt] = useState(
    DateTime.now()
  );
  const [hoveredIndex, setHoveredIndex] = useState<string | null>(null);

  const hasInsufficientBalance = () => {
    if (!balance?.available || rawAmount <= 0) {
      return false;
    }
    return Number(balance.available) < rawAmount;
  };

  const isImmediatePayment = () => {
    const today = DateTime.now().startOf('day');
    const selectedDate = transactionScheduleAt.startOf('day');
    return selectedDate.hasSame(today, 'day');
  };

  useEffect(() => {
    if (!remoteConfig) {
      dispatch(SettingsActions.getRemoteConfig());
    }
  }, [dispatch, remoteConfig]);

  const actions = [
    {
      icon: <Barcode />,
      title: translate('PAGES.HOME.PAY.ACTION.PAY_BILLET.TITLE'),
      text: translate('PAGES.HOME.PAY.ACTION.PAY_BILLET.TEXT'),
      onClick: () => {
        if (remoteConfig?.disableCashOut) {
          setOpenMaintenanceModal(true);

          return;
        }
        setPaymentType('billet');
        setTabKey('2');
        sendEvent(EventType.BOTTOM_MODAL_PAY_BILLET);
      },
      name: 'billet',
      hidden: !pay.billet,
    },
    {
      icon: <Pix />,
      title: translate('PAGES.HOME.PAY.ACTION.PAY_PIX.TITLE'),
      text: translate('PAGES.HOME.PAY.ACTION.PAY_PIX.TEXT'),
      onClick: () => {
        if (remoteConfig?.disableCashOut) {
          setOpenMaintenanceModal(true);

          return;
        }
        if (availableSoon.pix) {
          return MessageService.warn('APPLICATION.ERRORS.AVAILABLE_SOON');
        }

        navigate(getRouteStackPath('PIX', 'COPY_PASTE'));
      },
      name: 'pix',
      hidden: !pay.enablePayWithPix,
    },
  ].filter((action) => pix.enabled || action.name !== 'pix');

  useEffect(() => {
    if (transaction) {
      if (type === DetailType.NEW) {
        setTabKey('3');
      }
      if (transaction.status === TransactionStatus.PENDING_APPROVE) {
        navigate(
          `/aprovacao/detalhe-aprovacao/${transaction.transactionRequestPendingApprovalId}`
        );
        return;
      }

      if (!transfer.hasSmsConfirmation && type === DetailType.DETAIL) {
        setTabKey('3');

        NavigationService.navigate(
          `${getRouteStackPath('HOME', 'MY_ACCOUNT_DETAILS')}${
            transaction?.transactionRequestId
              ? transaction?.transactionRequestId
              : transaction?.id
          }`,
          {
            isExtract: true,
            isScheduled: !!transaction?.transactionScheduleAt,
          }
        );
      }
    }
  }, [navigate, transaction, type]);

  useEffect(() => {
    if (error) {
      if (!getEnumKeyByValue(error.message, BilletErrors)) {
        MessageService.error(`BILLET.${error.message}`, {
          originalError: error,
        });

        return;
      }

      MessageService.error(
        `BILLET.${getEnumKeyByValue(error.message, BilletErrors)}`
      );

      dispatch(DetailsAction.setDetailsError(null));
    }
  }, [dispatch, error]);

  useEffect(() => {
    if (tabKey === '1') {
      setPaymentType('billet');
    }
  }, [tabKey]);

  useEffect(() => {
    if (transaction?.billetInfo) {
      setRawAmount(transaction.billetInfo.totalAmount);
      setTransactionScheduleAt(DateTime.now());
    }
  }, [transaction?.billetInfo]);

  const isCodeRangeOk =
    unmaskField(code).length === 47 || unmaskField(code).length === 48;

  const handleBilletCodeSubmit = () => {
    const unmaskedCode = unmaskField(code);
    if (!isCodeRangeOk) {
      return;
    }
    dispatch(TransactionsActions.getBilletDetails(unmaskedCode));
  };

  const proceedToPayment = () => {
    transaction?.billetInfo?.totalAmount
      ? setIsPinModalVisible(true)
      : setConfirmationModalVisible(true);
  };

  const handleSubmitOnAdvancePaymentDetails = () => {
    const billetDueDate = transaction?.billetInfo?.dueDate;
    if (billetDueDate && !pay.allowBilletPaymentAfterDueDate) {
      const formattedBilletDueDate = DateTime.fromISO(billetDueDate);
      const today = DateTime.fromJSDate(new Date());
      const isPastDueDate =
        today.startOf('day') > formattedBilletDueDate.endOf('day');
      if (isPastDueDate) {
        MessageService.error('BILLET.PAST_DUE_DATE', { domain: 'validation' });
        return;
      }
    }

    if (!isImmediatePayment()) {
      setIsScheduleModalVisible(true);
      return;
    }

    proceedToPayment();
  };

  const typeOfDocument = (value: string) => {
    const documentLength = value.length;
    const typeableLineLength = 47;

    return documentLength === typeableLineLength ? 'typeableLine' : 'barcode';
  };

  const handlePinConfirmation = async (pin: string) => {
    setIsPinModalVisible(false);
    if (!transaction?.billetInfo) {
      return;
    }

    const timestamp = new Date().getTime().toString();

    const newBillet = new Billet(transaction.billetInfo);
    const amount = rawAmount ?? newBillet.calcTotal();

    const transactionHash = signData(
      amount * 100,
      transaction.billetInfo.barcode ?? transaction.billetInfo.typeableLine,
      timestamp
    );

    const documentType = typeOfDocument(
      newBillet.billet.barcode ?? newBillet.billet.typeableLine
    );

    const encryptedPIN = await encrypt(pin);
    dispatch(
      TransactionsActions.payBillet(
        newBillet,
        amount,
        transactionScheduleAt?.toISODate(),
        {
          pin: encryptedPIN,
          timestamp,
          transactionHash,
        },
        documentType
      )
    );
  };

  useEffect(() => {
    if (sendSmsForTransaction) {
      setIsSmsModalVisible(true);
      dispatch({
        type: SEND_TOTP_FOR_USER,
        payload: false,
      });
    }
  }, [sendSmsForTransaction, dispatch]);

  useEffect(() => {
    if (successTransferWithTotp) {
      dispatch({
        type: SET_SUCCESS_PIN_FOR_TOTP,
        payload: false,
      });
      navigate('/inicio/detalhes/' + transaction?.id);
    }
  }, [successTransferWithTotp, transaction?.id, dispatch, navigate]);

  const handleSmsConfirmation = (pin: string) => {
    dispatch(
      totpTransfer({ token: pin, transactionId: transaction?.id ?? '' })
    );
  };

  const resendTokenSms = () => {
    dispatch(
      requestTotpToken({
        contactType: 2,
        transactionId: transaction?.id ?? '',
      })
    );
  };

  if (!me) {
    return <></>;
  }

  const AdvanceBarCodeButton = (
    <div className="pay__button">
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
        variant="text"
        onClick={() => setTabKey('1')}
      />
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.ADVANCE')}
        variant="contained"
        onClick={handleBilletCodeSubmit}
        disabled={!isCodeRangeOk}
      />
    </div>
  );

  const AdvancePaymentDetails = (
    <div className="pay__button">
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
        variant="text"
        onClick={() => setTabKey('2')}
      />
      <AdvancedButton
        disabled={
          alertText !== '' ||
          rawAmount === 0 ||
          (hasInsufficientBalance() && isImmediatePayment())
        }
        text={translate('COMPONENTS.ADVANCED_BUTTON.ADVANCE')}
        onClick={handleSubmitOnAdvancePaymentDetails}
      />
    </div>
  );

  if (!me) {
    return <></>;
  }

  const stepOptions = () => {
    const steps = [
      {
        visible: true,
        value: TabIndexEnum.ACTIONS,
        label: translate('PAGES.HOME.PAY.TABS.PAYMENT_TYPE'),
        children: (
          <div className="pay__grid">
            {actions
              .filter((a) => !a.hidden)
              .map((action) => (
                <button
                  key={action.name}
                  className="pay__grid__button transparent-button"
                  onClick={action.onClick}
                  onMouseEnter={() => setHoveredIndex(action.name)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    border: `2px solid ${currentTheme.borderColor}`,
                    background:
                      hoveredIndex === action.name
                        ? currentTheme.background
                        : 'transparent',
                  }}
                >
                  <div
                    className="pay__grid__button__icon"
                    style={{ background: currentTheme.background }}
                  >
                    {action.icon}
                  </div>
                  <div>
                    <ThemedText
                      variant="body"
                      className="pay__grid__button__title"
                    >
                      {action.title}
                    </ThemedText>
                    <ThemedText className="pay__grid__button__description">
                      {action.text}
                    </ThemedText>
                  </div>
                </button>
              ))}
          </div>
        ),
      },
      {
        visible: !!paymentType,
        value: TabIndexEnum.BILLET_CODE,
        label: translate('PAGES.HOME.PAY.TABS.BARCODE'),
        children: (
          <>
            <BilletSlipCode
              code={code}
              setCode={setCode}
              handleBilletCodeSubmit={handleBilletCodeSubmit}
            />
            {AdvanceBarCodeButton}
          </>
        ),
      },
      {
        visible: true,
        value: TabIndexEnum.PAYMENT_DETAILS,
        label: translate('PAGES.HOME.PAY.TABS.PAYMENT_DETAILS'),
        children: (
          <>
            {transaction?.billetInfo ? (
              <>
                <PaymentDetails
                  billet={transaction.billetInfo}
                  rawAmount={rawAmount}
                  setRawAmount={setRawAmount}
                  alertText={alertText}
                  setAlertText={setAlertText}
                  setTransactionScheduleAt={setTransactionScheduleAt}
                  transactionScheduleAt={transactionScheduleAt}
                />
                {AdvancePaymentDetails}
              </>
            ) : null}
          </>
        ),
      },
    ];

    return (
      <AdvancedSteps
        testId="advanced-steps-pay"
        steps={steps.filter((step) => step.visible)}
        currentStep={tabKey}
        onStepChange={(step) => setTabKey(String(step))}
      />
    );
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            active: false,
            title: translate('PAGES.HOME.PAY.TITLE'),
          },
        ]}
      />
      <div className="pay">
        <AdvancedPanelContentHeader
          testId="pay-page-header"
          title={translate('PAGES.HOME.PAY.TITLE')}
          description={translate('PAGES.HOME.PAY.DESCRIPTION')}
          goBackAction={() => navigate.back()}
        />
        {stepOptions()}
      </div>
      <ConfirmationModal
        visible={isPinModalVisible}
        setVisible={setIsPinModalVisible}
        handleConfirmation={handlePinConfirmation}
      />
      <AdvancedModal
        title={translate('PAGES.HOME.PAY.CONFIRMATION_MODAL.TITLE')}
        size="medium"
        visible={confirmationModalVisible}
        onClose={() => setConfirmationModalVisible(false)}
        actions={[
          {
            label: translate(
              'PAGES.HOME.PAY.CONFIRMATION_MODAL.BUTTONS.CANCEL'
            ),
            onClick: () => setConfirmationModalVisible(false),
            type: 'cancel',
          },
          {
            label: translate('PAGES.HOME.PAY.CONFIRMATION_MODAL.BUTTONS.OK'),
            onClick: () => {
              setIsPinModalVisible(true);
              setConfirmationModalVisible(false);
            },
          },
        ]}
      >
        <div>
          <ThemedText>
            {translate('PAGES.HOME.PAY.CONFIRMATION_MODAL.MESSAGE.FIRST', {
              rawAmount: moneyFormat(rawAmount),
              code,
            })}
          </ThemedText>
          <br />
          <ThemedText>
            {translate('PAGES.HOME.PAY.CONFIRMATION_MODAL.MESSAGE.SECOND')}
          </ThemedText>
        </div>
      </AdvancedModal>
      <ConfirmationModal
        visible={isSmsModalVisible}
        setVisible={setIsSmsModalVisible}
        handleConfirmation={handleSmsConfirmation}
        resend={resendTokenSms}
        text={translate('PAGES.HOME.TRANSFER.SMS_CONFIRMATION_TEXT')}
        title={translate('PAGES.HOME.TRANSFER.SMS_CONFIRMATION_TITLE')}
        isSms
      />
      <AdvancedModal
        title={translate('PAGES.HOME.PAY.SCHEDULE_MODAL.TITLE')}
        size="small"
        visible={isScheduleModalVisible}
        onClose={() => setIsScheduleModalVisible(false)}
        actions={[
          {
            label: translate('PAGES.HOME.PAY.SCHEDULE_MODAL.BUTTONS.CANCEL'),
            onClick: () => setIsScheduleModalVisible(false),
            type: 'cancel',
          },
          {
            label: translate('PAGES.HOME.PAY.SCHEDULE_MODAL.BUTTONS.OK'),
            onClick: () => {
              setIsScheduleModalVisible(false);
              proceedToPayment();
            },
          },
        ]}
      >
        <div>
          <ThemedText>
            {translate('PAGES.HOME.PAY.SCHEDULE_MODAL.MESSAGE.FIRST', {
              date: transactionScheduleAt.toFormat('dd/MM/yyyy'),
            })}
          </ThemedText>
          <ThemedText>
            {translate('PAGES.HOME.PAY.SCHEDULE_MODAL.MESSAGE.SECOND')}
          </ThemedText>
        </div>
      </AdvancedModal>
      <MaintenanceModal
        setVisible={setOpenMaintenanceModal}
        visible={openMaintenanceModal}
      />
    </>
  );
};

export default Pay;
